/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { createTheme, Theme } from "@mui/material/styles"
import baseTheme from "@medaica/common/theme"

/**
 * The booking wizard's brand palette — the logo blue (indigo #3B3AEE), matching
 * patient.medaica.com. Centralized here so a palette change is one edit instead of
 * ~15 inline hex literals across the views/components.
 */
const bookingColors = {
  primary: "#3B3AEE", // logo blue — buttons, screen headings, active stepper pill, selected slot
  primaryLight: "#9898F6", // light indigo (primary-300)
  primaryMuted: "#6A69F2", // completed stepper pill (muted indigo)
  accent: "#3B3AEE", // active day pill (revisit in the Time-step redesign)
  surface: "#f6f6fa", // page background (portal neutral)
  surfaceMuted: "#EEEEFE", // upcoming stepper pill (light indigo tint)
  successBackground: "#d6f0e0", // Done check circle
  successForeground: "#1a7f4b",
}

/**
 * A booking-scoped MUI theme pinning the primary to the logo blue (indigo) so
 * `<Button variant="contained">` / `<LoadingButton>` match the rest of the flow and
 * patient.medaica.com. Inherits everything else from the base theme.
 */
const bookingTheme: Theme = createTheme(baseTheme, {
  palette: {
    primary: {
      main: bookingColors.primary,
      dark: "#1A1990",
      light: bookingColors.primaryLight,
      contrastText: "#ffffff",
    },
  },
})

/**
 * Wizard footer buttons (Back / Continue / Agree / Confirm): the base theme pins MuiButton at
 * 32px, which reads small next to the redesigned fields — this sx bumps ONLY the step-footer
 * buttons (not pagers/links) to the mockup's larger rounded shape. `height: "auto"` clears the
 * base theme's fixed height:32px so the box is content-sized — a long dynamic label (Time step's
 * "Confirm Tue, Sep 8, 4:30 AM") can wrap to two lines on a narrow phone and the background grows
 * with it; minHeight keeps the normal one-line case at the mockup's 46px.
 */
const footerButtonSx = { minHeight: 46, height: "auto", px: 4, borderRadius: "10px" } as const

export { bookingColors, footerButtonSx }
export default bookingTheme
